/**
 * Hash-router primitives shared by App.tsx's wouter `useHashLocation`
 * hook and any non-component code that needs to navigate (e.g.
 * SessionProvider, which is mounted outside wouter's <Router> and so
 * cannot call `useLocation()`).
 *
 * The app is primarily hash-routed so static hosts can serve a single
 * index.html for every URL. A small set of prefixes are served as real
 * paths so they can be deep-linked from email or admin dashboards.
 */
import { openDexLiveScanMenu } from "./dexLiveScanMenuState";

export const PATH_ROUTED_PREFIXES = ["/portal/admin/", "/auth/"];

/**
 * Window event the Pokédex page listens for so any app-wide "Live Scan"
 * affordance (NexFab, resume banner, profile CTA) can both navigate to
 * /pokedex AND pop the DexLiveScanMenu — even when /pokedex is already
 * the active route, where the navigation alone is a no-op.
 */
export const OPEN_DEX_LIVE_SCAN_EVENT = "dex:open-live-scan";

// Sticky intent: set when a Live Scan affordance fires while the Pokédex
// page may not yet be mounted (cross-route navigation). The page consumes
// it on mount so the menu still opens after the route transition settles.
let pendingDexLiveScanOpen = false;

export function requestDexLiveScan(): void {
  pendingDexLiveScanOpen = true;
  // The menu is hosted globally (GlobalDexLiveScanMenu in App.tsx), so it
  // paints NOW; the Pokédex page mounts underneath it.
  openDexLiveScanMenu();
  hashNavigate("/pokedex");
  if (typeof window !== "undefined") {
    // Fire the live event too so an already-mounted /pokedex opens at once.
    window.dispatchEvent(new Event(OPEN_DEX_LIVE_SCAN_EVENT));
  }
}

/** Consume the sticky intent (returns true once, then clears it). */
export function consumeDexLiveScanRequest(): boolean {
  if (!pendingDexLiveScanOpen) return false;
  pendingDexLiveScanOpen = false;
  return true;
}

/**
 * Normalize a stored deep-link href into a clean in-app route path that
 * `hashNavigate` understands (a leading-slash path with no `#`).
 *
 * Notification rows persist hrefs in a few shapes depending on when/where
 * they were written: `/#/friends` (hash-prefixed), `#/friends`, or a bare
 * `/friends`. The old tap handler blindly prepended another `#`, turning
 * `/#/friends` into the hash `#/#/friends` — which wouter reads as the path
 * `/#/friends`, matches no route, and lands on NotFound (the friend-add 404).
 *
 * Absolute URLs (http/https) and custom schemes (mailto:, pokemongo:) are
 * returned untouched so the caller can hand them to the browser directly.
 */
export function normalizeHashHref(href: string): { external: boolean; to: string } {
  const trimmed = href.trim();
  if (/^[a-z][a-z0-9+.-]*:/i.test(trimmed)) {
    return { external: true, to: trimmed };
  }
  // Strip any number of leading "/" and "#" so "/#/friends", "#/friends",
  // "/friends", and even "#friends" all collapse to the same route.
  let route = trimmed.replace(/^[/#]+/, "");
  route = "/" + route;
  return { external: false, to: route };
}

export const isPathRouted = (): boolean =>
  PATH_ROUTED_PREFIXES.some((p) => window.location.pathname.startsWith(p));

export const hashSubscribe = (cb: () => void): (() => void) => {
  window.addEventListener("hashchange", cb);
  window.addEventListener("popstate", cb);
  return () => {
    window.removeEventListener("hashchange", cb);
    window.removeEventListener("popstate", cb);
  };
};

export const hashGetSnapshot = (): string => {
  if (isPathRouted()) {
    return window.location.pathname || "/";
  }
  const hash = window.location.hash.replace(/^#/, "") || "/";
  const q = hash.indexOf("?");
  return q >= 0 ? hash.slice(0, q) : hash;
};

export const hashNavigate = (to: string, opts?: { replace?: boolean }): void => {
  const replace = opts?.replace ?? false;
  const targetIsPathRouted = PATH_ROUTED_PREFIXES.some((p) => to.startsWith(p));
  if (targetIsPathRouted) {
    if (replace) window.history.replaceState(null, "", to);
    else window.history.pushState(null, "", to);
    window.dispatchEvent(new PopStateEvent("popstate"));
    return;
  }
  // When we're currently on a path-routed URL (e.g. /portal/admin/captures)
  // and we're navigating to a hash route (e.g. RequireAuth bouncing an
  // unauthenticated visitor to "/signup"), we must reset the pathname back
  // to "/" first. Otherwise the resulting URL is something like
  // "/portal/admin/captures#/signup", which keeps isPathRouted() true so
  // hashGetSnapshot still returns "/portal/admin/captures" — the redirect
  // never takes effect and the user sees a blank screen behind the gate.
  if (isPathRouted()) {
    const hashTarget = to.startsWith("#") ? to : "#" + to;
    window.history.replaceState(null, "", "/" + hashTarget);
    // Notify the location hook that the URL changed. We dispatch both
    // events because hashSubscribe listens to both, and replaceState by
    // itself fires neither.
    window.dispatchEvent(new Event("hashchange"));
    window.dispatchEvent(new PopStateEvent("popstate"));
    return;
  }
  if (replace) {
    const hashTarget = to.startsWith("#") ? to : "#" + to;
    window.history.replaceState(null, "", hashTarget);
    // replaceState fires no event; nudge the location hook like the branch
    // above does.
    window.dispatchEvent(new Event("hashchange"));
    window.dispatchEvent(new PopStateEvent("popstate"));
    return;
  }
  window.location.hash = to;
};
